import { useEffect, useState } from 'preact/hooks';
import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import { Empty, ErrorNote, When } from './bits.js';

/**
 * The Access section: who can see one tenant from a signed-in console, and how
 * that changes.
 *
 * A tenant the operator published belongs to nobody. Until an address is put
 * on it here (or through the runbook in routes/dashboard.ts) no account can
 * open it, and the customer who was told "sign in and it will be there" signs
 * in to an empty Sites screen. This section is the console's side of that handoff.
 *
 * Three decisions shape the file, and every one of them is about who decides.
 *
 * THE SERVER DECIDES WHO MAY EDIT. The list comes back with `canManage`, and
 * that flag alone shows or hides the form. Nothing here reads the principal's
 * role: a client-side copy of "operator or owner" would be a second
 * authorisation rule, and the two would drift. When a POST or DELETE answers
 * 403 or 404 the form goes away and the server's sentence is printed verbatim,
 * because the sentence is the rule and this page has no better words for it.
 *
 * IT IS A REAL COMPONENT, NOT A VIEW FUNCTION. Every other piece of the
 * dashboard is a plain function over the store's state, and the store is where
 * "which button is busy" lives. This section is not: the store is being
 * reworked by hand right now, so this file carries its own state through
 * `preact/hooks` and touches neither `store.ts` nor `api.ts`. The cost is a
 * component instance in a tree of function calls, which Preact handles — the
 * caller keys it by tenant id so a different tenant gets a fresh one. The
 * request helper below is a deliberate copy of the client's `send`, kept to
 * the lines that matter: same-origin credentials so the session cookie
 * travels, the operator header only while a secret is actually held, and the
 * server's error text surfaced rather than replaced.
 *
 * NOTHING IS FABRICATED. A failed load is a sentence, never an empty list —
 * "nobody has access" is a conclusion an operator would act on, and it is not
 * what an unreachable API means. A response in a shape this file does not
 * recognise is a loud fault naming the field, not a row with a blank cell.
 *
 * Adding an address sends no email. The account row is created so the first
 * sign-in link the person asks for lands on it; the asking is theirs to do.
 */

/* ── Wire shapes, as routes/dashboard.ts sends them ────────────────────── */

export type MemberRole = 'owner' | 'member';

/** Mirrors `MEMBER_ROLES` in apps/server/src/dashboard/service.ts. */
export const MEMBER_ROLES: readonly MemberRole[] = ['owner', 'member'];

/** One account's access to one tenant. `email` is '' when the account row is gone. */
export interface MemberRow {
  accountId: string;
  email: string;
  role: MemberRole;
  /** ISO timestamp of the grant. */
  since: string;
}

export interface MemberList {
  members: MemberRow[];
  /** Whether the caller may change the list. The server's decision, not ours. */
  canManage: boolean;
}

export interface GrantReceipt {
  member: MemberRow;
  accountCreated: boolean;
  alreadyMember: boolean;
  members: MemberRow[];
}

export interface RevokeReceipt {
  removed: MemberRow;
  members: MemberRow[];
}

/* ── Readers: a shape mismatch is a fault with a field name on it ──────── */

/**
 * A response the server sent in a shape this file did not expect.
 *
 * Named after the path that was being read, for the same reason api.ts names
 * its own: a renamed field that arrived as `undefined` would otherwise paint
 * an empty email cell, and an operator would read that as an account with no
 * address rather than as a client that is out of date.
 */
export class MemberContractFault extends Error {
  constructor(
    readonly path: string,
    expected: string,
  ) {
    super(`the server's answer at ${path} is not ${expected}; this console may be out of date`);
    this.name = 'MemberContractFault';
  }
}

function obj(raw: unknown, path: string): Record<string, unknown> {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new MemberContractFault(path, 'an object');
  }
  return raw as Record<string, unknown>;
}

function str(raw: unknown, path: string): string {
  if (typeof raw !== 'string') throw new MemberContractFault(path, 'a string');
  return raw;
}

function bool(raw: unknown, path: string): boolean {
  if (typeof raw !== 'boolean') throw new MemberContractFault(path, 'a boolean');
  return raw;
}

function role(raw: unknown, path: string): MemberRole {
  if (raw !== 'owner' && raw !== 'member') {
    throw new MemberContractFault(path, `one of ${MEMBER_ROLES.map((r) => `"${r}"`).join(', ')}`);
  }
  return raw;
}

export function readMemberRow(raw: unknown, path: string): MemberRow {
  const o = obj(raw, path);
  return {
    accountId: str(o['accountId'], `${path}.accountId`),
    email: str(o['email'], `${path}.email`),
    role: role(o['role'], `${path}.role`),
    since: str(o['since'], `${path}.since`),
  };
}

function readMemberRows(raw: unknown, path: string): MemberRow[] {
  if (!Array.isArray(raw)) throw new MemberContractFault(path, 'an array');
  return raw.map((m, i) => readMemberRow(m, `${path}[${i}]`));
}

export function readMemberList(raw: unknown, path = 'members'): MemberList {
  const o = obj(raw, path);
  return {
    members: readMemberRows(o['members'], `${path}.members`),
    canManage: bool(o['canManage'], `${path}.canManage`),
  };
}

export function readGrant(raw: unknown, path = 'grant'): GrantReceipt {
  const o = obj(raw, path);
  return {
    member: readMemberRow(o['member'], `${path}.member`),
    accountCreated: bool(o['accountCreated'], `${path}.accountCreated`),
    alreadyMember: bool(o['alreadyMember'], `${path}.alreadyMember`),
    members: readMemberRows(o['members'], `${path}.members`),
  };
}

export function readRevoke(raw: unknown, path = 'revoke'): RevokeReceipt {
  const o = obj(raw, path);
  return {
    removed: readMemberRow(o['removed'], `${path}.removed`),
    members: readMemberRows(o['members'], `${path}.members`),
  };
}

/* ── Transport: the client's `send`, reduced to what this section needs ─── */

/** apps/server: `CLAWHARBOR_RELOAD_HEADER`, lower-cased, default below. */
const DEFAULT_SECRET_HEADER = 'x-clawharbor-reload-secret';

/** Same meta tag, same `?api` override, same precedence as api.ts. */
function apiBase(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="clawharbor-api"]');
  const configured = meta?.content?.trim();
  if (configured) return configured.replace(/\/+$/, '');
  const override = new URLSearchParams(location.search).get('api');
  if (override) return override.replace(/\/+$/, '');
  return '';
}

function secretHeaderName(): string {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="clawharbor-reload-header"], meta[name="clawharbor-publish-header"]',
  );
  return (meta?.content?.trim() || DEFAULT_SECRET_HEADER).toLowerCase();
}

/**
 * One answer, refused or not. A refusal is a VALUE here rather than a thrown
 * error because the status decides what the section does next — a 403 or 404
 * hides the form, a 401 hands over to the caller — and a thrown error would
 * make every handler re-derive that from the exception.
 */
export interface Answer {
  /** 0 when the request never reached a server. */
  status: number;
  ok: boolean;
  json: unknown;
  /** The server's own `error` text on a refusal; ours only when it sent none. */
  message: string;
  code: string;
}

async function send(method: string, path: string, secret: string, body?: unknown): Promise<Answer> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (secret) headers[secretHeaderName()] = secret;
  if (body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method,
      headers,
      // The session cookie is httpOnly; 'same-origin' is what makes it travel,
      // and it keeps a cross-origin `?api=` override from carrying it.
      credentials: 'same-origin',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    return {
      status: 0,
      ok: false,
      json: null,
      message: `the dashboard API is unreachable (${err instanceof Error ? err.message : String(err)})`,
      code: '',
    };
  }

  const text = await res.text().catch(() => '');
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text) as unknown;
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    const o = json !== null && typeof json === 'object' ? (json as Record<string, unknown>) : {};
    const message =
      (typeof o['error'] === 'string' && o['error']) ||
      (typeof o['message'] === 'string' && o['message']) ||
      (text ? text.slice(0, 240) : `${res.status} ${res.statusText}`);
    return { status: res.status, ok: false, json, message, code: typeof o['code'] === 'string' ? o['code'] : '' };
  }
  if (text && json === null) {
    return {
      status: res.status,
      ok: false,
      json: null,
      message: `the server answered ${path} with something other than JSON`,
      code: 'not_json',
    };
  }
  return { status: res.status, ok: true, json: json ?? {}, message: '', code: '' };
}

const enc = encodeURIComponent;

/**
 * The operator secret this page holds, or '' on an account session.
 *
 * The store mirrors the secret only while it is being typed and forgets it the
 * moment the unlock succeeds; from then on the one live copy is `api.secret`,
 * and index.ts publishes that object on `window.__clawharborDashboard` as the
 * page's single, documented incident handle. Reading it there is the only path
 * to the header that does not import the client this file is told to leave
 * alone. When the store grows an `actions.secret()` of its own, the caller
 * should hand that in instead and this function can go.
 */
export function heldOperatorSecret(): string {
  if (typeof window === 'undefined') return '';
  const handle = (window as unknown as { __clawharborDashboard?: { api?: { secret?: unknown } } })
    .__clawharborDashboard;
  const secret = handle?.api?.secret;
  return typeof secret === 'string' ? secret : '';
}

function membersPath(tenantId: string): string {
  return `/api/dashboard/tenants/${enc(tenantId)}/members`;
}

/* ── Styles ─────────────────────────────────────────────────────────────── */

/**
 * The section's own rules, injected once into the document head.
 *
 * `dashboard.css` is compiled separately from the JS bundle and a JS-side
 * `import './members.css'` would make esbuild emit a second `dashboard.css`
 * over the first; a string next to the component that uses it is the same
 * trick apps/web uses for the widget sheet, and it keeps this file droppable.
 * Everything is namespaced under `.access` and leans on the shared tokens, so
 * it reads as the same instrument panel without touching the shared sheet.
 */
const ACCESS_CSS = `
.access { margin-top: 42px; }
.access__list { list-style: none; margin: 0 0 18px; padding: 0; border-top: var(--bw) solid var(--rule); }
.access__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  gap: 8px 14px;
  align-items: center;
  padding: 12px 0;
  border-bottom: var(--bw) solid var(--rule);
}
.access__row[aria-busy="true"] { opacity: .6; }
.access__email { min-width: 0; font-weight: 600; overflow-wrap: anywhere; }
.access__email--gone { font-family: var(--mono); font-weight: 400; color: var(--dim); }
.access__role {
  font-family: var(--mono);
  font-size: var(--t-micro);
  letter-spacing: .18em;
  text-transform: uppercase;
  color: var(--dim);
  white-space: nowrap;
}
.access__role--owner { color: var(--signal); }
.access__since { font-size: var(--t-code); color: var(--text-2); white-space: nowrap; }
.access__act { display: flex; gap: 8px; align-items: center; justify-content: flex-end; flex-wrap: wrap; }
.access__act .btn { min-height: var(--control-md); padding-block: 6px; }
.access__ask { font-size: var(--t-code); color: var(--text-2); }
.access__form { display: grid; gap: 10px; max-width: 44rem; }
.access__fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 14rem) auto;
  gap: 10px;
  align-items: end;
}
.access__field { display: grid; gap: 4px; min-width: 0; }
.access__label { font-family: var(--mono); font-size: var(--t-label); color: var(--dim); }
.access__input, .access__select {
  width: 100%;
  min-width: 0;
  min-height: var(--control-lg);
  padding: 10px 12px;
  border: var(--bw) solid var(--edge);
  border-radius: var(--r-control);
  background: var(--ground);
  color: var(--text);
  font-family: var(--sans);
  font-size: var(--t-sm);
}
.access__hint { margin: 0; font-size: var(--t-code); color: var(--dim); max-width: 68ch; }
.access__notice {
  margin: 0 0 16px;
  padding: 12px 16px;
  border-left: 3px solid var(--ok);
  background: color-mix(in srgb, var(--ok) 8%, transparent);
  color: var(--text);
  font-size: var(--t-sm);
  overflow-wrap: anywhere;
}
.access__readonly { margin: 0; font-size: var(--t-code); color: var(--text-2); max-width: 68ch; }
.access__loading { margin: 0; font-family: var(--mono); font-size: var(--t-code); letter-spacing: .12em; text-transform: uppercase; color: var(--dim); }
@media (max-width: 640px) {
  .access__row { grid-template-columns: minmax(0, 1fr) auto; }
  .access__since { grid-column: 1 / -1; }
  .access__act { grid-column: 1 / -1; justify-content: flex-start; }
  .access__fields { grid-template-columns: 1fr; }
}
`;

let stylesInjected = false;
function ensureStyles(): void {
  if (stylesInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.id = 'clawharbor-access-css';
  el.textContent = ACCESS_CSS;
  document.head.append(el);
  stylesInjected = true;
}

/* ── The section ─────────────────────────────────────────────────────────── */

export interface MembersSectionProps {
  tenantId: string;
  /**
   * The operator secret held in memory, read per request; '' on an account
   * session. A getter rather than a value because the store forgets its copy
   * the moment the unlock succeeds, and the live copy is on the api object the
   * caller can reach and this file must not import.
   */
  secret: () => string;
  now: number;
  /**
   * A 401 is the session ending, not a failure of this section. The caller owns
   * the login screen, so it is told and decides; the sentence is still printed
   * here in case it does nothing.
   */
  onUnauthorized?: () => void;
}

interface AccessState {
  phase: 'loading' | 'ready' | 'failed';
  members: MemberRow[];
  canManage: boolean;
  /** Why the list could not be loaded. Never an empty list in its place. */
  loadError: string | null;

  email: string;
  role: MemberRole;
  adding: boolean;
  addError: string | null;

  /** The last thing that worked, in words, for the live region. */
  notice: string | null;

  /** The account whose Remove button awaits its second press. */
  confirming: string | null;
  removing: string | null;
  removeError: string | null;
}

function initial(): AccessState {
  return {
    phase: 'loading',
    members: [],
    canManage: false,
    loadError: null,
    email: '',
    role: 'owner',
    adding: false,
    addError: null,
    notice: null,
    confirming: null,
    removing: null,
    removeError: null,
  };
}

/** What a successful grant says. Every clause is a fact the server sent. */
export function grantSentence(r: GrantReceipt): string {
  const who = r.member.email || r.member.accountId;
  if (r.alreadyMember) {
    return `${who} was already on this tenant as ${r.member.role}; nothing changed.`;
  }
  const head = `${who} now has access as ${r.member.role}.`;
  return r.accountCreated
    ? `${head} No account existed for that address; the first sign-in link it asks for will land on this tenant.`
    : head;
}

export function MembersSection(props: MembersSectionProps): VNode {
  const { tenantId, now } = props;
  ensureStyles();
  const [s, set] = useState<AccessState>(initial);
  const patch = (p: Partial<AccessState>): void => set((prev) => ({ ...prev, ...p }));

  /** The two refusals that mean "not yours to change", and the one that means "not you any more". */
  const refused = (a: Answer): Partial<AccessState> => {
    if (a.status === 401) props.onUnauthorized?.();
    return a.status === 403 || a.status === 404 || a.status === 401 ? { canManage: false } : {};
  };

  useEffect(() => {
    let live = true;
    set(initial());
    void send('GET', membersPath(tenantId), props.secret()).then((a) => {
      if (!live) return;
      if (!a.ok) {
        if (a.status === 401) props.onUnauthorized?.();
        patch({ phase: 'failed', loadError: a.message });
        return;
      }
      try {
        const list = readMemberList(a.json, `GET ${membersPath(tenantId)}`);
        patch({ phase: 'ready', members: list.members, canManage: list.canManage, loadError: null });
      } catch (err) {
        patch({ phase: 'failed', loadError: err instanceof Error ? err.message : String(err) });
      }
    });
    return () => {
      live = false;
    };
    // The secret getter and the 401 hook are stable for the life of the page;
    // only the tenant decides which list this is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const add = async (): Promise<void> => {
    const email = s.email.trim();
    if (!email || s.adding) return;
    patch({ adding: true, addError: null, notice: null });
    const a = await send('POST', membersPath(tenantId), props.secret(), { email, role: s.role });
    if (!a.ok) {
      patch({ adding: false, addError: a.message, ...refused(a) });
      return;
    }
    try {
      const r = readGrant(a.json, `POST ${membersPath(tenantId)}`);
      patch({ adding: false, email: '', members: r.members, notice: grantSentence(r) });
    } catch (err) {
      patch({ adding: false, addError: err instanceof Error ? err.message : String(err) });
    }
  };

  const remove = async (m: MemberRow): Promise<void> => {
    if (s.removing) return;
    patch({ removing: m.accountId, confirming: null, removeError: null, notice: null });
    const a = await send('DELETE', `${membersPath(tenantId)}/${enc(m.accountId)}`, props.secret());
    if (!a.ok) {
      patch({ removing: null, removeError: a.message, ...refused(a) });
      return;
    }
    try {
      const r = readRevoke(a.json, `DELETE ${membersPath(tenantId)}/${m.accountId}`);
      const who = r.removed.email || r.removed.accountId;
      patch({
        removing: null,
        members: r.members,
        notice: `${who} no longer has access. Any session they hold sees one tenant fewer on its next request.`,
      });
    } catch (err) {
      patch({ removing: null, removeError: err instanceof Error ? err.message : String(err) });
    }
  };

  const emailId = `access-email-${tenantId}`;
  const roleId = `access-role-${tenantId}`;
  const owners = s.members.filter((m) => m.role === 'owner').length;

  return html`
    <section class="access" aria-labelledby="access-h">
      <h2 class="sec__h" id="access-h">Access</h2>
      <p class="sec__p">
        Who sees <code>${tenantId}</code> on a signed-in console. Anyone listed can open this page,
        edit its tools and views, and test them; an <b>owner</b> can also change this list. Nobody
        listed can publish on the site's behalf or write to it — the tools stay read-only whoever
        holds the key.
      </p>

      ${s.notice
        ? html`<p class="access__notice" role="status" aria-live="polite">${s.notice}</p>`
        : html`<p class="sr-only" role="status" aria-live="polite"></p>`}

      ${s.phase === 'loading'
        ? html`<p class="access__loading" aria-busy="true">Loading who has access…</p>`
        : s.phase === 'failed'
          ? ErrorNote({ message: s.loadError, label: 'Could not load the access list.' })
          : s.members.length === 0
            ? Empty({
                title: 'Nobody has access to this site.',
                body: 'It is live and the operator of this host can see it, but no signed-in account can — until an address is added below. Adding one does not email anybody; it means the next time that address signs in, this site is on their list.',
              })
            : html`<ul class="access__list" aria-label=${`Accounts with access to ${tenantId}`}>
                ${s.members.map((m) => MemberItem({
                  m,
                  now,
                  canManage: s.canManage,
                  confirming: s.confirming === m.accountId,
                  removing: s.removing === m.accountId,
                  lastOwner: m.role === 'owner' && owners === 1,
                  busy: s.removing !== null,
                  onAsk: () => patch({ confirming: m.accountId, removeError: null }),
                  onKeep: () => patch({ confirming: null }),
                  onRemove: () => void remove(m),
                }))}
              </ul>`}

      ${ErrorNote({ message: s.removeError, label: 'Not removed.' })}

      ${s.phase === 'ready' && s.canManage
        ? html`
            <form
              class="access__form"
              aria-label=${`Give an address access to ${tenantId}`}
              onSubmit=${(e: Event) => {
                e.preventDefault();
                void add();
              }}
            >
              <div class="access__fields">
                <div class="access__field">
                  <label class="access__label" for=${emailId}>Email address</label>
                  <input
                    id=${emailId}
                    class="access__input"
                    type="email"
                    name="email"
                    required
                    autocomplete="off"
                    spellcheck="false"
                    autocapitalize="off"
                    placeholder="person@example.com"
                    value=${s.email}
                    disabled=${s.adding}
                    onInput=${(e: Event) => patch({ email: (e.target as HTMLInputElement).value, addError: null })}
                  />
                </div>
                <div class="access__field">
                  <label class="access__label" for=${roleId}>Role</label>
                  <select
                    id=${roleId}
                    class="access__select"
                    name="role"
                    value=${s.role}
                    disabled=${s.adding}
                    onChange=${(e: Event) => patch({ role: role((e.target as HTMLSelectElement).value, 'role') })}
                  >
                    <option value="owner">owner — can change this list</option>
                    <option value="member">member — sees and edits the tenant</option>
                  </select>
                </div>
                <button type="submit" class="btn" disabled=${s.adding || s.email.trim().length === 0}>
                  ${s.adding ? 'Adding…' : 'Give access'}
                </button>
              </div>
              ${ErrorNote({ message: s.addError, label: 'Not added.' })}
              <p class="access__hint">
                No email is sent. If the address has never signed in, an account is created for it
                now so the first sign-in link it asks for lands here. An address already on the list
                keeps its role; this form never demotes.
              </p>
            </form>
          `
        : s.phase === 'ready'
          ? html`<p class="access__readonly">
              ${s.addError ?? 'You can see this list because you are on it. Changing it takes an owner of the tenant, or the operator.'}
            </p>`
          : null}
    </section>
  `;
}

/** One row. The remove is two presses on purpose: the second one names the address. */
function MemberItem(props: {
  m: MemberRow;
  now: number;
  canManage: boolean;
  confirming: boolean;
  removing: boolean;
  /** The only owner left. The server refuses an owner who tries; the row says why first. */
  lastOwner: boolean;
  busy: boolean;
  onAsk: () => void;
  onKeep: () => void;
  onRemove: () => void;
}): VNode {
  const { m } = props;
  const who = m.email || m.accountId;
  return html`
    <li class="access__row" aria-busy=${props.removing ? 'true' : 'false'}>
      <span class=${m.email ? 'access__email' : 'access__email access__email--gone'} title=${m.accountId}>
        ${m.email ? m.email : html`account ${m.accountId} — no account row`}
      </span>
      <span class=${`access__role access__role--${m.role}`}>${m.role}</span>
      <span class="access__since">
        since${SP}${When({ iso: m.since, now: props.now, absent: 'unknown' })}
      </span>
      <span class="access__act">
        ${!props.canManage
          ? null
          : props.confirming
            ? html`
                <span class="access__ask" id=${`access-ask-${m.accountId}`}>
                  Remove ${who}?${props.lastOwner ? ' They are the only owner.' : ''}
                </span>
                <button
                  type="button"
                  class="btn btn--danger"
                  aria-describedby=${`access-ask-${m.accountId}`}
                  disabled=${props.busy}
                  onClick=${props.onRemove}
                >
                  Yes, remove
                </button>
                <button type="button" class="btn btn--ghost" disabled=${props.busy} onClick=${props.onKeep}>
                  Keep
                </button>
              `
            : html`
                <button
                  type="button"
                  class="btn btn--ghost"
                  aria-label=${`Remove ${who}`}
                  disabled=${props.busy}
                  onClick=${props.onAsk}
                >
                  ${props.removing ? 'Removing…' : 'Remove'}
                </button>
              `}
      </span>
    </li>
  `;
}
